import React from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Hourglass, LogOut, MailCheck } from "lucide-react";

/**
 * Shown to internal / client users whose `status !== "active"`.
 *
 * Without this, the post-login flow goes:
 *   /auth/me → 423 → AuthContext sets `{ _pending: true, status, _label }` (no role)
 *   → ProtectedRoute sees no role → redirects to "/" → Layout re-mounts → redirect loop
 *   → user sees a blank screen.
 *
 * Now the AuthContext stub lands here instead and the user gets a clear, friendly state
 * with the only action that makes sense for them: log out and wait for the admin email.
 */
export default function PendingApprovalScreen() {
  const { user, logout } = useAuth();
  const status = user?.status || "submitted";
  const label = user?._label || (status === "invited"
    ? "Awaiting your details"
    : status === "changes_requested"
      ? "Your admin asked for a couple of edits"
      : "Awaiting admin approval");
  const message = status === "invited"
    ? "Your account is set up but the founders haven't received your onboarding details yet. Check your email for the invitation link."
    : status === "changes_requested"
      ? "An admin sent your application back with notes. Re-check your inbox for the latest magic-link to update your details."
      : "Your details are in. The founders will review and activate your account — you'll get an email the moment it's done. Hang tight.";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-br from-slate-50 to-amber-50">
      <div className="card-flat max-w-lg w-full p-8 sm:p-10 text-center" data-testid="pending-approval-screen">
        <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-4">
          <Hourglass size={26} />
        </div>
        <div className="label-overline text-amber-700">Analogy Hub</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-1">{label}</h1>
        <p className="text-sm text-slate-600 mt-3 leading-relaxed">{message}</p>
        {user?.email && (
          <div className="mt-5 inline-flex items-center gap-2 text-[12px] bg-slate-100 text-slate-700 rounded-sm px-3 py-1.5">
            <MailCheck size={13} /> Signed in as <b>{user.email}</b>
          </div>
        )}
        <div className="mt-6 flex justify-center">
          <Button
            variant="outline"
            onClick={logout}
            className="rounded-sm"
            data-testid="pending-approval-logout"
          >
            <LogOut size={14} className="mr-1.5" /> Sign out
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-6">If this is taking longer than a day, ping <b>founders@analogydesign.co</b>.</p>
      </div>
    </div>
  );
}
